import { Link } from "react-router-dom";
import {
  Printer, Download, ArrowLeft, Quote, ArrowDown, Clock, Users, Truck, Route as RouteIcon,
  Layers, CloudRain, Sprout, Leaf, ShieldAlert, Wrench, DollarSign, TrendingDown,
  PiggyBank, Percent, Scale, CalendarClock, Timer, HardHat, Activity, ShieldCheck,
  CheckCircle2, XCircle, Gauge as GaugeIcon,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

const INK = "#0B192C";
const FLAME = "#FF4500";

/* ---------- small visual primitives ---------- */
function Ring({ value, max = 500, label, sub, color = FLAME }) {
  const r = 52, c = 2 * Math.PI * r;
  const pct = Math.min(value / max, 1);
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 130 130" className="w-32 h-32">
        <circle cx="65" cy="65" r={r} fill="none" stroke="#E5E7EB" strokeWidth="11" />
        <circle cx="65" cy="65" r={r} fill="none" stroke={color} strokeWidth="11" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)} transform="rotate(-90 65 65)" />
        <text x="65" y="62" textAnchor="middle" className="font-display" fontSize="24" fontWeight="800" fill={INK}>{label}</text>
        <text x="65" y="82" textAnchor="middle" fontSize="10" fill="#6B7280" style={{ letterSpacing: "0.08em" }}>{sub}</text>
      </svg>
    </div>
  );
}

function Scorecard({ label, value, accent }) {
  return (
    <div className="border border-border bg-white p-5">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`mt-2 font-display font-extrabold text-2xl ${accent ? "text-flame" : "text-ink"}`}>{value}</div>
    </div>
  );
}

function ScenarioCard({ tag, title, duration, invest, avoid, net, roi, payback, featured }) {
  return (
    <div className={`border ${featured ? "border-flame shadow-xl" : "border-border"} bg-white flex flex-col`}>
      <div className={`px-6 py-4 flex items-center justify-between ${featured ? "bg-flame text-white" : "bg-ink text-white"}`}>
        <span className="font-display font-extrabold text-lg">{tag}</span>
        <span className="text-[11px] uppercase tracking-widest opacity-80">{duration}</span>
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="text-sm font-medium text-charcoal">{title}</div>
        <div className="mt-6 flex items-center gap-5">
          <Ring value={roi} max={450} label={`${roi}%`} sub="ROI" color={featured ? FLAME : INK} />
          <div className="flex-1 space-y-2">
            <Row k="Investment" v={invest} />
            <Row k="Cost Avoidance" v={avoid} strong />
            <Row k="Net Benefit" v={net} flame />
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="bg-muted p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Payback</div>
            <div className="font-display font-extrabold text-xl text-ink">{payback}</div>
          </div>
          <div className="bg-muted p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Return on Investment</div>
            <div className="font-display font-extrabold text-xl text-flame">{roi}%</div>
          </div>
        </div>
      </div>
    </div>
  );
}
const Row = ({ k, v, strong, flame }) => (
  <div className="flex items-baseline justify-between border-b border-border pb-1.5">
    <span className="text-xs text-muted-foreground">{k}</span>
    <span className={`font-mono font-semibold ${flame ? "text-flame" : "text-ink"} ${strong ? "text-base" : "text-sm"}`}>{v}</span>
  </div>
);

/* ---------- data ---------- */
const HIDDEN_COSTS = [
  "Equipment downtime", "Labor inefficiency", "Weather delays", "Temporary access construction",
  "Site restoration", "Equipment recovery", "Environmental incidents", "Schedule extensions",
];
const FLOW = [
  { t: "Initial Purchase Price", d: "The visible line item" },
  { t: "Operational Productivity", d: "Uptime, throughput, crew efficiency" },
  { t: "Schedule Performance", d: "Fewer delays, faster completion" },
  { t: "Risk Reduction", d: "Safety, environmental & damage exposure" },
  { t: "Total Installed Project Cost", d: "The number that matters", final: true },
];
const DAILY = [
  { label: "Equipment productivity", val: 500 },
  { label: "Labor efficiency", val: 275 },
  { label: "Reduced rework", val: 300 },
  { label: "Schedule acceleration", val: 700 },
  { label: "Reduced restoration", val: 180 },
  { label: "Risk reduction", val: 160 },
];
const DRIVERS = [
  { icon: Clock, t: "Equipment downtime", d: "Idle machines waiting on stable ground." },
  { icon: Users, t: "Labor waiting", d: "Crews standing by during access failures." },
  { icon: Truck, t: "Machine recovery", d: "Extracting bogged-down equipment." },
  { icon: RouteIcon, t: "Temporary road construction", d: "Gravel, timber & rebuild cycles." },
  { icon: Layers, t: "Soft ground delays", d: "Lost days on unstable subgrade." },
  { icon: CloudRain, t: "Rain delays", d: "Weather shutdowns on bare ground." },
  { icon: Sprout, t: "Site restoration", d: "Regrading and re-vegetation at closeout." },
  { icon: Leaf, t: "Environmental remediation", d: "Rutting, runoff & sensitive-area impact." },
  { icon: ShieldAlert, t: "Safety incidents", d: "Slips, tips & unstable-surface events." },
  { icon: Wrench, t: "Equipment damage", d: "Wear from operating on poor ground." },
];
const KPIS = [
  { icon: DollarSign, label: "Investment", value: "$25,000" },
  { icon: TrendingDown, label: "Est. Cost Avoidance", value: "$82,000", flame: true },
  { icon: PiggyBank, label: "Net Financial Benefit", value: "$57,000", flame: true },
  { icon: Percent, label: "Return on Investment", value: "228%", flame: true },
  { icon: Scale, label: "Benefit-to-Cost Ratio", value: "3.3 : 1" },
  { icon: CalendarClock, label: "Payback Period", value: "14 Days" },
  { icon: Timer, label: "Schedule Days Recovered", value: "~7 Days" },
  { icon: HardHat, label: "Equipment Hours Recovered", value: "~160 hrs" },
  { icon: Activity, label: "Labor Hours Recovered", value: "~300 hrs" },
  { icon: ShieldCheck, label: "Risk Exposure Reduced", value: "~65%" },
];

/* ---------- section shell ---------- */
const Sec = ({ n, title, children, dark, muted }) => (
  <section className={`${dark ? "bg-ink text-white" : muted ? "bg-muted" : "bg-white"} print:break-inside-avoid`}>
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20">
      <div className="flex items-center gap-4 mb-8">
        <span className={`font-mono text-sm ${dark ? "text-flame" : "text-flame"}`}>{n}</span>
        <span className={`h-px flex-1 ${dark ? "bg-white/15" : "bg-border"}`} />
      </div>
      <h2 className={`font-display font-extrabold text-3xl md:text-4xl tracking-tight ${dark ? "text-white" : "text-ink"}`}>{title}</h2>
      <div className="mt-8">{children}</div>
    </div>
  </section>
);

export default function ProcurementBrief() {
  return (
    <div className="bg-white min-h-screen">
      {/* Document top bar */}
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to product
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Executive Briefing · Confidential</span>
            <a href="/big-bad-mats-procurement-brief.pdf" download data-testid="download-brief" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="print-brief" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>

      {/* COVER */}
      <section className="relative overflow-hidden bg-ink text-white">
        <img src={IMAGES.bulldozer} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/60" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-center gap-3">
            <img src={IMAGES.markWhite} alt="OneHarvest" className="h-9 w-9 object-contain" />
            <span className="font-display font-extrabold tracking-tight">ONEHARVEST</span>
          </div>
          <div className="mt-14 inline-flex items-center gap-2 border border-flame/50 bg-flame/10 text-flame px-3 py-1.5 text-[11px] uppercase tracking-[0.2em]">
            Executive Procurement Brief
          </div>
          <h1 className="mt-6 font-display font-black text-5xl md:text-7xl tracking-tight">Big Bad Mats</h1>
          <p className="mt-4 font-display font-bold text-xl md:text-2xl text-white/90">Procurement Cost Benefit &amp; Unit Economics Analysis</p>
          <p className="mt-2 italic text-flame text-lg">Reducing Total Project Cost Through Engineered Ground Protection</p>

          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-3">Prepared for</div>
              <div className="flex flex-wrap gap-2">
                {["Procurement Directors", "Strategic Sourcing", "Category Managers", "Construction Ops Directors", "Project Executives", "Estimators", "Fleet Managers", "CFOs", "Project Controls"].map((a) => (
                  <span key={a} className="text-xs border border-white/20 px-3 py-1.5 text-white/80">{a}</span>
                ))}
              </div>
            </div>
            <div className="border-l-2 border-flame pl-5 flex items-center">
              <p className="text-white/85 leading-relaxed">
                Big Bad Mats should be evaluated as a <span className="text-flame font-semibold">Total Project Cost Reduction investment</span> — not simply as a rental or equipment purchase.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 1 — EXECUTIVE SUMMARY */}
      <Sec n="01" title="Executive Summary">
        <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
          Procurement decisions are often driven by purchase price. Yet on heavy civil construction projects, the largest costs are not generated at the point of purchase — they are generated in the field, through the hidden operational losses that accumulate across a project's life.
        </p>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {HIDDEN_COSTS.map((c) => (
            <div key={c} className="flex items-start gap-3 border border-border bg-muted/40 p-4">
              <span className="mt-1.5 h-1.5 w-1.5 bg-flame flex-shrink-0" />
              <span className="text-sm text-charcoal">{c}</span>
            </div>
          ))}
        </div>
        <p className="mt-8 text-lg text-charcoal leading-relaxed max-w-3xl">
          Positioned correctly, Big Bad Mats functions as <span className="font-semibold text-ink">infrastructure that reduces these hidden project costs</span> — converting a line-item expense into a measurable driver of project economics.
        </p>
        <div className="mt-10 relative border border-border bg-ink text-white p-8 md:p-10">
          <Quote className="h-8 w-8 text-flame mb-4" />
          <p className="font-display font-bold text-2xl md:text-3xl leading-snug">
            "The lowest purchase price rarely delivers the lowest total project cost."
          </p>
        </div>
      </Sec>

      {/* SECTION 2 — WHY TOTAL COST */}
      <Sec n="02" title="Why Procurement Should Evaluate Total Cost" muted>
        <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
          Procurement should optimize <span className="font-semibold text-ink">lifecycle value</span> rather than initial expenditure. Each layer below compounds into the number that actually appears on the project P&amp;L.
        </p>
        <div className="mt-10 max-w-2xl mx-auto">
          {FLOW.map((f, i) => (
            <div key={f.t}>
              <div className={`flex items-center gap-5 border p-5 ${f.final ? "border-flame bg-flame text-white" : "border-border bg-white"}`}>
                <span className={`font-display font-extrabold text-lg ${f.final ? "text-white" : "text-flame"}`}>{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <div className={`font-display font-bold text-lg ${f.final ? "text-white" : "text-ink"}`}>{f.t}</div>
                  <div className={`text-sm ${f.final ? "text-white/80" : "text-muted-foreground"}`}>{f.d}</div>
                </div>
              </div>
              {i < FLOW.length - 1 && <div className="flex justify-center py-2"><ArrowDown className="h-5 w-5 text-flame" /></div>}
            </div>
          ))}
        </div>
      </Sec>

      {/* SECTION 3 — SCENARIOS */}
      <Sec n="03" title="Scenario-Based Financial Analysis">
        <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
          Three representative deployments demonstrate how return scales with project size and complexity.
        </p>
        <div className="mt-10 grid lg:grid-cols-3 gap-6">
          <ScenarioCard tag="Scenario A" title="Small Site Access Project" duration="30-day" invest="$18,000" avoid="$23,200" net="$5,200" roi={29} payback="23 Days" />
          <ScenarioCard tag="Scenario B" title="Medium Civil Construction Project" duration="45-day" invest="$25,000" avoid="$82,000" net="$57,000" roi={228} payback="14 Days" featured />
          <ScenarioCard tag="Scenario C" title="Large Infrastructure Project" duration="60-day" invest="$35,000" avoid="$184,500" net="$149,500" roi={427} payback="8 Days" />
        </div>
      </Sec>

      {/* SECTION 4 — UNIT ECONOMICS */}
      <Sec n="04" title="Unit Economics Breakdown" muted>
        <div className="grid md:grid-cols-3 gap-3 mb-10">
          {[["10", "Heavy equipment assets"], ["25", "Person field crew"], ["45", "Day project"], ["$250/hr", "Equipment operating cost"], ["$75/hr", "Burdened labor"], ["3.8 : 1", "Benefit-to-cost ratio"]].map(([v, l]) => (
            <div key={l} className="bg-white border border-border p-4">
              <div className="font-display font-extrabold text-2xl text-ink">{v}</div>
              <div className="text-xs text-muted-foreground mt-1">{l}</div>
            </div>
          ))}
        </div>

        <h3 className="font-display font-bold text-xl text-ink mb-4">Estimated daily economic value created</h3>
        <div className="bg-white border border-border p-6 md:p-8">
          <div className="space-y-4">
            {DAILY.map((d) => (
              <div key={d.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-charcoal">{d.label}</span>
                  <span className="font-mono font-semibold text-ink">${d.val}/day</span>
                </div>
                <div className="h-3 bg-muted overflow-hidden">
                  <div className="h-full bg-flame" style={{ width: `${(d.val / 700) * 100}%` }} />
                </div>
              </div>
            ))}
            <div className="pt-4 border-t border-border flex justify-between items-center">
              <span className="font-display font-bold text-ink">Total Daily Economic Benefit</span>
              <span className="font-display font-extrabold text-2xl text-flame">$2,115/day</span>
            </div>
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Scorecard label="45-Day Value Created" value="$95,175" />
          <Scorecard label="Big Bad Mats Investment" value="$25,000" />
          <Scorecard label="Net Financial Benefit" value="$70,175" accent />
          <Scorecard label="Benefit-to-Cost Ratio" value="3.8 : 1" accent />
        </div>
      </Sec>

      {/* SECTION 5 — COST DRIVERS */}
      <Sec n="05" title="Cost Drivers Eliminated">
        <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
          Engineered access solutions attack the largest hidden project costs at their source.
        </p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {DRIVERS.map((d) => (
            <div key={d.t} className="border border-border bg-white p-6 hover:border-flame/50 transition-colors">
              <d.icon className="h-7 w-7 text-flame" />
              <div className="mt-4 font-display font-bold text-ink">{d.t}</div>
              <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{d.d}</div>
            </div>
          ))}
        </div>
      </Sec>

      {/* SECTION 6 — DECISION MATRIX */}
      <Sec n="06" title="Procurement Decision Matrix" muted>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border border-border bg-white">
            <div className="bg-charcoal text-white px-6 py-4 flex items-center gap-3">
              <XCircle className="h-5 w-5 text-white/70" />
              <span className="font-display font-bold text-lg">Traditional Procurement</span>
            </div>
            <ul className="p-6 space-y-3">
              {["Lowest upfront cost", "Price-focused", "Reactive", "Unknown lifecycle costs"].map((x) => (
                <li key={x} className="flex items-center gap-3 text-charcoal"><span className="h-1.5 w-1.5 bg-charcoal/40" /> {x}</li>
              ))}
            </ul>
          </div>
          <div className="border-2 border-flame bg-white shadow-lg">
            <div className="bg-flame text-white px-6 py-4 flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-display font-bold text-lg">Value-Based Procurement</span>
            </div>
            <ul className="p-6 space-y-3">
              {["Lowest total project cost", "Productivity-focused", "Risk-managed", "Quantifiable ROI", "Faster project completion", "Reduced operational variability"].map((x) => (
                <li key={x} className="flex items-center gap-3 text-ink font-medium"><CheckCircle2 className="h-4 w-4 text-flame flex-shrink-0" /> {x}</li>
              ))}
            </ul>
          </div>
        </div>
      </Sec>

      {/* SECTION 7 — EXECUTIVE DASHBOARD */}
      <Sec n="07" title="Executive Financial Dashboard" dark>
        <p className="text-white/60 max-w-3xl">Representative 45-day civil construction project. Figures are illustrative and vary by site conditions, equipment mix, and schedule.</p>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
          {KPIS.map((k) => (
            <div key={k.label} className="bg-ink p-6">
              <k.icon className={`h-6 w-6 ${k.flame ? "text-flame" : "text-white/70"}`} />
              <div className={`mt-4 font-display font-extrabold text-2xl ${k.flame ? "text-flame" : "text-white"}`}>{k.value}</div>
              <div className="mt-1 text-[11px] uppercase tracking-widest text-white/50">{k.label}</div>
            </div>
          ))}
        </div>
      </Sec>

      {/* SECTION 8 — RECOMMENDATION */}
      <Sec n="08" title="Procurement Recommendation">
        <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
          Big Bad Mats should be evaluated as a <span className="font-semibold text-ink">project cost optimization strategy</span> rather than a commodity purchase. While unit price may exceed lower-cost alternatives, measurable reductions in downtime, schedule delays, equipment recovery, restoration, and operational risk can substantially improve overall project economics.
        </p>
        <div className="mt-10 relative border-l-4 border-flame bg-ink text-white p-8 md:p-10">
          <p className="font-display font-extrabold text-2xl md:text-3xl leading-snug">
            "The objective is not to purchase the least expensive mat. The objective is to deliver the lowest total installed project cost."
          </p>
        </div>

        <div className="mt-12 border-t border-border pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="font-display font-bold text-ink">Discuss a project-specific analysis</div>
            <div className="text-sm text-muted-foreground mt-1">{COMPANY.phone} · {COMPANY.email}</div>
          </div>
          <Link to="/request-quote" className="inline-flex items-center gap-2 bg-flame text-white px-6 py-4 font-semibold hover:bg-flame/90 transition-colors print:hidden">
            Request a Tailored Analysis
          </Link>
        </div>
      </Sec>

      <div className="bg-ink text-white/40 text-center text-xs py-6 px-6">
        © {new Date().getFullYear()} {COMPANY.legal}. Executive Procurement Brief — figures are illustrative estimates provided for evaluation purposes only. Not a quote, guarantee, or warranty of any specific result, and not financial, engineering, or legal advice. Actual outcomes vary by site conditions, equipment mix &amp; schedule.
      </div>
    </div>
  );
}
